import { api } from './api';
import {
  IMessageDto,
  CreateMessageResponse,
  UserConversationEntity,
  MessageEntity,
} from './chatApi';

/** Shape actually returned by GET /user-conversations/:id/messages. */
export interface MatchmakerMessagesResponse {
  userConversation: UserConversationEntity;
  messages: { data: MessageEntity[]; meta: { hasMore: boolean } };
}

/**
 * Endpoints for the admin "Matchmaker Chat" tool. Every call impersonates a
 * target user via the `x-login-as` header (honored by the backend only for
 * admins). The header is injected per-request so the global prepareHeaders
 * (which sets the admin's Bearer token) is left untouched.
 *
 * The tool mirrors the real customer flow: there is ONE matchmaker conversation
 * per user (`GET /users/profile-conversation` get-or-creates it and seeds the
 * agent's opening message for brand-new users). Messages are then fetched
 * paginated from `GET /user-conversations/:id/messages`.
 */

export interface CreateTestUserResponse {
  id: string;
  email: string;
  userType?: number;
}

/** Build the headers for an impersonated (+ optional prompt-preview) request. */
const mmHeaders = (
  impersonateUserId: string,
  promptOverrides?: Record<string, number>
): Record<string, string> => {
  const headers: Record<string, string> = { 'x-login-as': impersonateUserId };
  if (promptOverrides && Object.keys(promptOverrides).length) {
    headers['x-mm-prompt-overrides'] = JSON.stringify(promptOverrides);
  }
  return headers;
};

export const matchmakerChatApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Get-or-create the impersonated user's single matchmaker conversation.
    // For a brand-new user this also seeds the agent's opening message, exactly
    // like the real customer flow.
    getMatchmakerConversation: builder.query<
      UserConversationEntity,
      { impersonateUserId: string }
    >({
      query: ({ impersonateUserId }) => ({
        url: '/users/profile-conversation',
        method: 'GET',
        headers: mmHeaders(impersonateUserId),
      }),
    }),
    fetchMatchmakerMessages: builder.query<
      MatchmakerMessagesResponse,
      {
        impersonateUserId: string;
        userConversationId: string;
        limit?: number;
        createdAt__lt?: string;
      }
    >({
      query: ({ impersonateUserId, userConversationId, ...params }) => ({
        url: `/user-conversations/${userConversationId}/messages`,
        method: 'GET',
        params,
        headers: mmHeaders(impersonateUserId),
      }),
    }),
    sendMatchmakerMessage: builder.mutation<
      CreateMessageResponse,
      {
        impersonateUserId: string;
        userConversationId: string;
        message: IMessageDto;
        promptOverrides?: Record<string, number>;
      }
    >({
      query: ({
        impersonateUserId,
        userConversationId,
        promptOverrides,
        ...body
      }) => ({
        url: `/user-conversations/${userConversationId}/messages`,
        method: 'POST',
        body,
        headers: mmHeaders(impersonateUserId, promptOverrides),
      }),
    }),
    createTestUser: builder.mutation<
      CreateTestUserResponse,
      { email?: string } | void
    >({
      query: (body) => ({
        url: '/users/admin/test-user',
        method: 'POST',
        body: body || {},
      }),
    }),
  }),
});

export const {
  useLazyGetMatchmakerConversationQuery,
  useLazyFetchMatchmakerMessagesQuery,
  useSendMatchmakerMessageMutation,
  useCreateTestUserMutation,
} = matchmakerChatApi;
